/**
 * Logger - Shared Logging Infrastructure
 *
 * Provides consistent logging across all modules with configurable levels.
 *
 * Usage:
 *   import { createLogger } from './logger.js';
 *   const log = createLogger('ModuleName');
 *   log.info('Something happened');
 *   log.error('Something broke', error);
 */

// Log levels
export const LOG_LEVELS = {
  none: 0,
  error: 1,
  warn: 2,
  info: 3,
  debug: 4,
};

// Detect production environment
const isProduction =
  typeof window !== "undefined" &&
  window.location &&
  !window.location.hostname.includes("localhost") &&
  !window.location.hostname.includes("127.0.0.1");

// Default level: warn in production, info in development
const DEFAULT_LEVEL = isProduction ? LOG_LEVELS.warn : LOG_LEVELS.info;

// Global override (can be set via window.ORBITAL_LOG_LEVEL)
function getGlobalOverride() {
  if (typeof window !== "undefined" && window.ORBITAL_LOG_LEVEL !== undefined) {
    const level = window.ORBITAL_LOG_LEVEL;
    if (typeof level === "number" && level >= 0 && level <= 4) {
      return level;
    }
    if (typeof level === "string" && LOG_LEVELS[level] !== undefined) {
      return LOG_LEVELS[level];
    }
  }
  return null;
}

// Store per-module log levels
const moduleLevels = new Map();

/**
 * Create a logger for a module
 * @param {string} moduleName - Name to prefix in log output (e.g., 'CRDT Bridge', 'Store')
 * @param {string|number} [defaultLevel] - Default level for this module ('warn', 'info', etc. or 0-4)
 * @returns {Object} Logger instance with error, warn, info, debug methods
 */
export function createLogger(moduleName, defaultLevel) {
  // Resolve initial level
  let level;
  const globalOverride = getGlobalOverride();
  if (globalOverride !== null) {
    level = globalOverride;
  } else if (defaultLevel !== undefined) {
    level = typeof defaultLevel === "string" ? LOG_LEVELS[defaultLevel] ?? DEFAULT_LEVEL : defaultLevel;
  } else {
    level = DEFAULT_LEVEL;
  }

  // Store for per-module control
  moduleLevels.set(moduleName, level);

  const logger = {
    /**
     * Set log level for this module
     * @param {string|number} newLevel - 'none'|'error'|'warn'|'info'|'debug' or 0-4
     */
    setLevel(newLevel) {
      if (typeof newLevel === "string" && LOG_LEVELS[newLevel] !== undefined) {
        level = LOG_LEVELS[newLevel];
      } else if (typeof newLevel === "number" && newLevel >= 0 && newLevel <= 4) {
        level = newLevel;
      } else {
        console.warn(`[${moduleName}] Invalid log level: ${newLevel}`);
        return;
      }
      moduleLevels.set(moduleName, level);
    },

    /**
     * Get current log level
     * @returns {number}
     */
    getLevel() {
      return level;
    },

    /**
     * Log an error (level 1+)
     */
    error(...args) {
      if (level >= LOG_LEVELS.error) {
        console.error(`[${moduleName}]`, ...args);
      }
    },

    /**
     * Log a warning (level 2+)
     */
    warn(...args) {
      if (level >= LOG_LEVELS.warn) {
        console.warn(`[${moduleName}]`, ...args);
      }
    },

    /**
     * Log info (level 3+)
     */
    info(...args) {
      if (level >= LOG_LEVELS.info) {
        console.log(`[${moduleName}]`, ...args);
      }
    },

    /**
     * Log debug (level 4)
     */
    debug(...args) {
      if (level >= LOG_LEVELS.debug) {
        console.log(`[${moduleName} DEBUG]`, ...args);
      }
    },
  };

  return logger;
}

/**
 * Set log level for all modules
 * @param {string|number} newLevel - 'none'|'error'|'warn'|'info'|'debug' or 0-4
 */
export function setGlobalLogLevel(newLevel) {
  let resolvedLevel;
  if (typeof newLevel === "string" && LOG_LEVELS[newLevel] !== undefined) {
    resolvedLevel = LOG_LEVELS[newLevel];
  } else if (typeof newLevel === "number" && newLevel >= 0 && newLevel <= 4) {
    resolvedLevel = newLevel;
  } else {
    console.warn(`Invalid global log level: ${newLevel}`);
    return;
  }

  // Update window global for new loggers
  if (typeof window !== "undefined") {
    window.ORBITAL_LOG_LEVEL = resolvedLevel;
  }

  // Note: Existing loggers keep their level until setLevel is called on them
  // This is intentional - allows per-module overrides
}

/**
 * Get all registered module loggers and their levels (for debugging)
 * @returns {Object} Map of module names to levels
 */
export function getLoggerLevels() {
  const result = {};
  for (const [name, level] of moduleLevels) {
    result[name] = level;
  }
  return result;
}

// Expose for debugging
if (typeof window !== "undefined") {
  window.__OrbitalLoggers = {
    setGlobalLevel: setGlobalLogLevel,
    getLevels: getLoggerLevels,
    LEVELS: LOG_LEVELS,
  };
}
